import React, { useEffect, useState } from 'react';
import api from '../utils/api';

const markets = [
  { region: 'EU / Europe', name: 'EU Emissions Trading System', price: 'EUR 60-70 / tCO2e', color: 'var(--neon-green)', desc: 'The largest mature compliance market, with deep allowance pricing and strong policy signaling.' },
  { region: 'United Kingdom', name: 'UK ETS', price: 'GBP 35-45 / tCO2e', color: 'var(--neon-cyan)', desc: 'A separate post-Brexit market with power, industry, and aviation exposure.' },
  { region: 'China', name: 'National ETS (CEA)', price: 'CNY 95-100 / tCO2e', color: 'var(--neon-orange)', desc: 'The largest market by emissions coverage and a useful comparison for intensity-based design.' },
  { region: 'USA / California', name: 'California Cap-and-Trade', price: 'USD 35-40 / tCO2e', color: 'var(--neon-pink)', desc: 'A long-running subnational system with linked market dynamics and strong policy structure.' },
  { region: 'Global / Voluntary', name: 'VCM (Verra / Gold Standard)', price: 'USD 5-50 / tCO2e', color: 'var(--neon-purple)', desc: 'A broad voluntary market where integrity, methodology, and project quality drive pricing spread.' },
  { region: 'India', name: 'Indian Carbon Market', price: 'INR 2,800-3,200 / tCO2e', color: 'var(--neon-yellow)', desc: 'A developing intensity-based market with compliance and offset pathways still moving toward operational scale.' },
];

export default function GlobalMarket() {
  const [prices, setPrices] = useState(null);

  useEffect(() => {
    api.get('/market/prices').then((r) => setPrices(r.data)).catch(() => {});
  }, []);

  return (
    <div className="page-shell page-stack">
      <section className="surface-panel glow-surface" style={{ padding: '1.8rem' }}>
        <div className="section-stack">
          <div className="section-tag">International markets</div>
          <h1 className="section-title">Global market context redesigned as a cleaner comparison layer</h1>
          <p className="section-copy">
            Instead of reading like a dense encyclopedia block, this page now acts more like a comparison shelf: what each market is, how it prices, and where India fits in that landscape.
          </p>
        </div>
      </section>

      {prices && (
        <section className="metric-strip">
          <div className="metric-tile">
            <div className="eyebrow">CCC reference</div>
            <div className="value" style={{ color: 'var(--neon-green)' }}>Rs.{prices.spot.ccc_mandatory.toLocaleString('en-IN')}</div>
          </div>
          <div className="metric-tile">
            <div className="eyebrow">EU ETS INR equivalent</div>
            <div className="value" style={{ color: 'var(--neon-cyan)' }}>Rs.{prices.spot.eu_ets_inr?.toLocaleString('en-IN')}</div>
          </div>
          <div className="metric-tile">
            <div className="eyebrow">China CEA INR equivalent</div>
            <div className="value" style={{ color: 'var(--neon-orange)' }}>Rs.{prices.spot.china_cea_inr?.toLocaleString('en-IN')}</div>
          </div>
          <div className="metric-tile">
            <div className="eyebrow">Source</div>
            <div className="value" style={{ color: 'var(--neon-pink)', fontSize: '1.2rem' }}>{prices.source?.type === 'reference_snapshot' ? 'Reference' : 'Connected'}</div>
          </div>
        </section>
      )}

      <section className="surface-panel" style={{ padding: '1.5rem' }}>
        <div className="section-header-row">
          <div className="section-stack">
            <div className="section-tag" style={{ color: 'var(--neon-blue)' }}>Comparison grid</div>
            <div className="headline-md">How the major carbon markets compare at a glance</div>
          </div>
        </div>

        <div className="market-grid-3" style={{ marginTop: '1rem' }}>
          {markets.map((market) => (
            <div key={market.name} className="market-card">
              <div className="eyebrow">{market.region}</div>
              <div className="headline-sm" style={{ marginTop: '0.35rem' }}>{market.name}</div>
              <div className="headline-md" style={{ color: market.color, marginTop: '0.9rem', fontSize: '1.9rem' }}>{market.price}</div>
              <div className="copy-sm" style={{ marginTop: '0.65rem' }}>{market.desc}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="section-grid-2">
        <div className="surface-panel" style={{ padding: '1.5rem' }}>
          <div className="section-stack">
            <div className="section-tag">Why this matters</div>
            <div className="headline-md">Global context helps users judge what is mature, what is developing, and what is still reference-only.</div>
            <div className="copy-sm">
              A cleaner UI should not just look better. It should also reduce misinterpretation. This page is meant to show that India belongs in a global class of carbon markets while still being honest about operational maturity.
            </div>
          </div>
        </div>

        <div className="surface-panel" style={{ padding: '1.5rem' }}>
          <div className="section-stack">
            <div className="section-tag" style={{ color: 'var(--neon-yellow)' }}>India note</div>
            <div className="headline-md">India remains a rule-backed market moving toward fuller execution</div>
            <div className="copy-sm">
              The product keeps pricing and market context explicit: reference snapshots today, scalable exchange connectivity later. That is a better UX than implying a live global trading terminal when the official feed layer is not there yet.
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
